import { assertUnreachable } from '../../utils/assertUnreachable';
import { isLegacyTeamHubDesktop } from '../../utils/desktopApp';
import { getAccessToken, getIdToken, isHostedByTeamHub } from '../hostAuthService';
import {
  isOpenMeetingRoom,
  type MeetingWindowAuthProvider,
  requestMeetingSwitch,
  reserveMeetingWindow,
} from './meetingWindowManager';
import { openMeetingInExternalWindow } from './openMeetingInExternalWindow';

/**
 * The hosted-mode meeting launch, with no React dependency.
 *
 * Split out of `useLaunchMeeting` so the same launch can be driven from outside
 * the React tree — specifically by the `meet` inter-module API, which the
 * TeamHub shell calls for meeting links clicked anywhere in the app (SM-1254).
 * Meet's remote is only mounted while the Meet tab is active, so a link clicked
 * in Chat has no mounted hook to call.
 *
 * `useLaunchMeeting` delegates here rather than duplicating the branching, so
 * the desktop/legacy/blocked decisions cannot drift between the two callers.
 * What stays in the hook is what genuinely needs React: the blocked-popup
 * dialog and the standalone in-page navigation.
 */

export type LaunchMeetingStatus =
  /** The meeting window was opened (or handed to the system browser on legacy desktop). */
  | 'launched'
  /** A meeting window was already open on this room and has been focused. */
  | 'existing'
  /**
   * A different meeting is running, so the meeting window is asking the user
   * whether to leave it for this one. Meet owns the outcome — the caller must
   * not also open the room.
   */
  | 'confirming'
  /** The browser blocked the popup. The caller owns the fallback UX. */
  | 'blocked'
  /** Not running hosted by TeamHub — the caller owns the standalone behaviour. */
  | 'declined';

export interface LaunchMeetingCoreResult {
  status: LaunchMeetingStatus;
  /**
   * The identity provider the launch used, so a caller handling `blocked` can
   * retry the popup with the same identity instead of building a second one.
   */
  getAuth: MeetingWindowAuthProvider;
}

export interface LaunchMeetingCoreParams {
  meetingId: string;
  password?: string | null;
  /**
   * Overrides the host identity provider. React callers pass the one from
   * `useUser` so the popup gets the same tokens the rest of the app is using;
   * callers outside React get the `hostAuthService` default below.
   */
  getAuth?: MeetingWindowAuthProvider;
}

/**
 * Reads the host tokens straight from `window.PKCEAuth`, which needs no React
 * context — this is what makes an imperative launch possible.
 */
export const getHostAuthSnapshot: MeetingWindowAuthProvider = async () => ({
  idToken: await getIdToken(),
  accessToken: await getAccessToken(),
});

export const launchMeetingCore = ({
  meetingId,
  password,
  getAuth = getHostAuthSnapshot,
}: LaunchMeetingCoreParams): LaunchMeetingCoreResult => {
  if (!isHostedByTeamHub()) {
    return { status: 'declined', getAuth };
  }

  // Desktop shells without their own meeting window must not be asked to
  // reserve one: the `about:blank` reservation reaches the OS and Windows turns
  // it into a Store prompt. Hand them the room directly instead — they route it
  // to the system browser (SM-1186).
  if (isLegacyTeamHubDesktop()) {
    openMeetingInExternalWindow(meetingId, password);
    return { status: 'launched', getAuth };
  }

  // Current desktop builds intercept this reservation and open a main-process
  // window while keeping the postMessage auth/settings bridge (SM-1186).
  const result = reserveMeetingWindow({ getAuth });
  switch (result.status) {
    case 'existing':
      // Already on this room: focusing it (which the reservation just did) is
      // the whole job.
      if (isOpenMeetingRoom(meetingId)) {
        return { status: 'existing', getAuth };
      }
      // A different meeting is running. Replacing it silently would drop the
      // user out of a live call, so ask in that window first. If the window
      // vanished in between, fall back to reporting it as focused rather than
      // claiming a prompt the user will never see.
      return requestMeetingSwitch({ meetingId, password })
        ? { status: 'confirming', getAuth }
        : { status: 'existing', getAuth };
    case 'reserved':
      result.handle.navigate(meetingId, password);
      return { status: 'launched', getAuth };
    case 'blocked':
      return { status: 'blocked', getAuth };
    default:
      return assertUnreachable(result);
  }
};
